import { Button } from '@/components/Button';
import type { DataStreamDefinitionId } from '@squaredup/data-streams';
import type { Serialised } from '@squaredup/ids';
import type { TenantId } from '@squaredup/tenants';
import LoadingSpinner from 'components/LoadingSpinner';
import Modal, { ifNotOutside, ModalButtons } from 'components/Modal';
import { get } from 'lodash';
import { type SaveAsPluginDependencies } from 'pages/datasources/components/saveAsPlugin/useSaveAsPluginDependencies';
import { useSaveAsPluginMutation } from 'pages/datasources/components/saveAsPlugin/useSaveAsPluginMutation';
import { useAllPlugins } from 'queries/hooks/useAllPlugins';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm, type UseFormClearErrors, type UseFormSetError } from 'react-hook-form';
import { useNavigate } from 'react-router';
import Auth from 'services/Auth';
import { useCurrentWorkspaceId } from 'services/WorkspaceUtil';
import { Tabs, type TabOption } from 'ui/Tabs';
import { BasicsTab } from './BasicsTab';
import { ConfigurationTab } from './ConfigurationTab';
import { DashboardsTab } from './DashboardsTab';
import { DataStreamsTab } from './DataStreamsTab';

export type PluginSaveFormData = {
    displayName: string;
    author: string;
    description?: string;
    helpLink?: string;
    uiConfig: unknown;
    uiConfigMapping: Record<string, unknown>;
    dataStreams?: Record<string, boolean>;
    icon?: string;
};

const getErrorMessage = (error: unknown): string => {
    if (error == null || typeof error !== 'object') {
        return String(error);
    }

    const message = get(error, 'message', undefined);

    const responseError = get(error, 'response.data.error', undefined);

    return responseError ?? message ?? String(error);
};

const updateDataStreamValidation = (
    selectedDataStreamCount: number,
    setError: UseFormSetError<PluginSaveFormData>,
    clearErrors: UseFormClearErrors<PluginSaveFormData>
) => {
    if (selectedDataStreamCount === 0) {
        setError('dataStreams', {
            type: 'manual',
            message: 'At least one data stream must be selected.'
        });
    } else {
        clearErrors('dataStreams');
    }
};

export const SaveAsPluginModalContent: React.FC<{
    pluginId: string;
    dependencies: SaveAsPluginDependencies;
    onClose: () => void;
}> = ({ pluginId, dependencies, onClose }) => {
    const navigate = useNavigate();
    const workspaceId = useCurrentWorkspaceId();

    const { data: plugins = [] } = useAllPlugins();

    const {
        saveAsPluginAsync,
        isLoading: isSavingAsPlugin,
        error,
        isError: saveFailed
    } = useSaveAsPluginMutation(dependencies);

    const initialValues = {
        author: Auth.user?.name ?? '',
        dataStreams: Object.fromEntries(
            dependencies.basePluginDataStreams
                ?.filter((ds) => ds.parentPluginId !== pluginId)
                .map((ds) => [ds.id, true]) ?? []
        ),
        uiConfig: []
    };

    const form = useForm<PluginSaveFormData>({ defaultValues: initialValues, mode: 'all' });
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);

    const selectedDataStreamCount = Object.values(form.watch('dataStreams') ?? {}).filter((x) => x).length;
    useEffect(() => {
        updateDataStreamValidation(selectedDataStreamCount, form.setError, form.clearErrors);
    }, [form.clearErrors, form.setError, selectedDataStreamCount]);

    const isFormValid = Object.keys(form.formState.errors).length === 0 && form.formState.isValid;

    const tabs: TabOption[] = [
        {
            label: 'Basics',
            component: <BasicsTab existingPluginNames={plugins.map((p) => p.displayName ?? '')} />,
            key: 'basics'
        },
        {
            label: 'Data Streams',
            component: (
                <DataStreamsTab basePluginId={pluginId} dataStreams={dependencies.basePluginDataStreams ?? []} />
            ),
            key: 'data-streams'
        },
        {
            label: 'Dashboards',
            component: <DashboardsTab />,
            key: 'dashboards'
        },
        {
            label: 'Configuration',
            component: (
                <ConfigurationTab
                    rawUiConfig={form.watch('uiConfig')}
                    basePluginDataSourceConfig={dependencies.basePluginConfig?.config ?? {}}
                    uiConfigMapping={form.watch('uiConfigMapping')}
                />
            ),
            key: 'configuration'
        }
    ];

    const onSubmit = async (data: PluginSaveFormData) => {
        try {
            await saveAsPluginAsync({
                displayName: data.displayName,
                author: data.author,
                description: data.description ?? '',
                category: 'User Defined',
                uiConfiguration: data.uiConfig ?? [],
                restrictToTenant: dependencies.tenantId as unknown as Serialised<TenantId>,
                uiConfigMapping: data.uiConfigMapping,
                dataStreamsToCopy:
                    data.dataStreams == null
                        ? undefined
                        : Object.entries(data.dataStreams)
                              .filter(([, value]) => value)
                              .map(([key]) => key as DataStreamDefinitionId['value']),
                icon: data.icon,
                helpLink: data.helpLink
            });

            onClose();

            navigate(`/datasources/${workspaceId}?search=${encodeURIComponent(data.displayName)}`);
        } catch (e) {
            if (import.meta.env.DEV) {
                // eslint-disable-next-line no-console
                console.error('Error saving plugin:', e);
            }
        }
    };

    return (
        <Modal
            modalClassName='max-w-[80vw] min-w-[40vw]'
            title='Save as Data Source'
            close={ifNotOutside(() => {
                if (!form.formState.isSubmitting) {
                    onClose();
                }
            })}
            fullHeight={true}
            headerBorder={true}
        >
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col flex-1'>
                    <div className='flex-1 basis-0'>
                        <fieldset disabled={form.formState.isSubmitting} className='h-full'>
                            <Tabs
                                tabs={tabs.map((t) => ({
                                    ...t,
                                    panelClassName: 'flex-1 basis-0 scroll-overflow'
                                }))}
                                tabsID='pluginSaveTabs'
                                selectedIndex={selectedTabIndex}
                                onSelect={setSelectedTabIndex}
                                forceRenderTabPanel={true}
                            />
                        </fieldset>
                    </div>
                    <ModalButtons hideTopMargin>
                        {saveFailed && error && (
                            <div className='my-auto'>
                                <p className='text-red-500'>{getErrorMessage(error)}</p>
                            </div>
                        )}
                        <Button
                            type='button'
                            variant='tertiary'
                            className='capitalize'
                            disabled={form.formState.isSubmitting}
                            onClick={() => {
                                onClose();
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type='submit'
                            className='capitalize'
                            disabled={!isFormValid || form.formState.isSubmitting || isSavingAsPlugin}
                        >
                            {form.formState.isSubmitting && <LoadingSpinner size={18} className='mr-3' />}

                            <span>Save</span>
                        </Button>
                    </ModalButtons>
                </form>
            </FormProvider>
        </Modal>
    );
};
